/**
 * parseNumber — conversion mots-nombres français → entier (JS pur, ZÉRO IA).
 *
 * Vosk transcrit les nombres en LETTRES (« chambre cent un » et non « 101 »).
 * Ce module les ramène à des entiers de façon déterministe (pilier #1 du plan :
 * alternative déterministe avant tout appel IA). Couvre 0–9999 (numéros de
 * chambre, et plus tard les constantes type « cent trente »).
 */

const SMALL: Record<string, number> = {
  zéro: 0, zero: 0, un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5,
  six: 6, sept: 7, huit: 8, neuf: 9, dix: 10, onze: 11, douze: 12, treize: 13,
  quatorze: 14, quinze: 15, seize: 16,
  vingt: 20, trente: 30, quarante: 40, cinquante: 50, soixante: 60,
};

function normalize(s: string): string {
  return s.toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Convertit des mots-nombres français en entier (0–9999), ou null si aucun
 * mot-nombre n'est présent. Les mots inconnus (« chambre »…) sont ignorés.
 * Gère les idiomes : « quatre-vingt(s) » = 80, « soixante-dix » = 70 (60+10),
 * « cent un » = 101, « trois cent dix-huit » = 318, « vingt et un » = 21.
 */
export function frenchWordsToNumber(text: string): number | null {
  const words = normalize(text).split(' ');
  let result = 0;
  let current = 0;
  let seen = false;

  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    if (w === 'et') continue;
    // « quatre-vingt(s) » = 80 (≠ quatre + vingt)
    if (w === 'quatre' && (words[i + 1] === 'vingt' || words[i + 1] === 'vingts')) {
      current += 80;
      i++;
      seen = true;
      continue;
    }
    if (w === 'cent' || w === 'cents') {
      current = (current === 0 ? 1 : current) * 100;
      seen = true;
      continue;
    }
    if (w === 'mille' || w === 'milles') {
      result += (current === 0 ? 1 : current) * 1000;
      current = 0;
      seen = true;
      continue;
    }
    if (w in SMALL) {
      current += SMALL[w];
      seen = true;
      continue;
    }
    // mot inconnu → ignoré (ex. « chambre », « box »)
  }

  return seen ? result + current : null;
}

/**
 * Extrait un entier d'un transcript : priorité aux chiffres s'ils sont présents
 * (« chambre 101 »), sinon repli sur les mots-nombres français (« chambre cent un »).
 */
export function parseSpokenInt(text: string): number | null {
  const digits = text.match(/\d{1,4}/);
  if (digits) return Number(digits[0]);
  return frenchWordsToNumber(text);
}
